/**
 * Lucky dice, single-player only: online battles never use them, so the
 * shared engine and PvP balance stay untouched.
 *
 * - Battle dice: one charge per fight, plus one more each time the hero wins
 *   three rounds in a row (max 2 held). A roll heals the hero and/or hurts the
 *   monster by a share of max HP.
 * - Victory roll: after a win, one roll adds 0-100% to the battle's rewards.
 */

export type DieFace = 1 | 2 | 3 | 4 | 5 | 6;

/** A fair die from the browser's crypto RNG (tests pass their own). */
export function rollDie(random: () => number = cryptoRandom): DieFace {
  return (Math.floor(random() * 6) + 1) as DieFace;
}

function cryptoRandom(): number {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return (buf[0] ?? 0) / 2 ** 32;
}

export interface BattleDiceFace {
  /** % of the hero's max HP healed. */
  healPercent: number;
  /** % of the monster's max HP dealt. */
  damagePercent: number;
  /** English text (translated by the scene). */
  text: string;
}

export const BATTLE_DICE: Record<DieFace, BattleDiceFace> = {
  1: { healPercent: 0, damagePercent: 0, text: 'The die rolls off the desk. Nothing happens!' },
  2: { healPercent: 15, damagePercent: 0, text: 'A sticky plaster! Heal {heal} HP.' },
  3: { healPercent: 0, damagePercent: 10, text: 'Paper cut! {damage} damage.' },
  4: { healPercent: 0, damagePercent: 15, text: 'Ruler smack! {damage} damage.' },
  5: { healPercent: 10, damagePercent: 10, text: 'Lucky doodle! Heal {heal} HP and deal {damage} damage.' },
  6: { healPercent: 0, damagePercent: 22, text: 'CRITICAL ROLL! {damage} damage!' },
};

export const DICE_MAX_CHARGES = 2;
/** Round-win streak that earns another charge. */
export const DICE_STREAK = 3;

export interface DiceRollResult {
  face: DieFace;
  heal: number;
  damage: number;
  hp: [number, number];
}

/** Applies a battle roll to hp ([hero, monster]). Never heals above max; damage can knock the monster out. */
export function applyBattleDice(face: DieFace, hp: readonly [number, number], maxHp: readonly [number, number]): DiceRollResult {
  const f = BATTLE_DICE[face];
  const heal = Math.min(maxHp[0] - hp[0], Math.ceil((maxHp[0] * f.healPercent) / 100));
  const damage = Math.min(hp[1], Math.ceil((maxHp[1] * f.damagePercent) / 100));
  return { face, heal, damage, hp: [hp[0] + heal, hp[1] - damage] };
}

/** Extra reward % for a victory roll. */
export const VICTORY_BONUS: Record<DieFace, number> = { 1: 0, 2: 10, 3: 20, 4: 30, 5: 50, 6: 100 };
